import {
  CallEvent,
  EventType,
  GetCallPathResponse_Person,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-path/v1/api.pb';
import { CustomerLifecycleManagementOrg } from '@wetags/customer-lifecycle-management';
import { isBefore } from 'date-fns';
import { CallPathV1 } from '@frontend/api-call-path';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { getFullName } from '@frontend/name';
import { useHasPhoneSettingsAccess } from '@frontend/phone-config';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Text, useTooltip } from '@frontend/design-system';
import { formatTime } from '../utils/get-event-accordion-body';
import { renderEvent } from '../utils/render-event';
import { EventBox } from './event-renderer-item';
import { CallPathMappingHeader } from './header';

type CallPathMappingProps = {
  callPathDetails?: CallPathV1.CallPathTypes.GetCallPathIO['output'];
};

export const CallPathMapping = ({ callPathDetails }: CallPathMappingProps) => {
  const { t } = useTranslation('calls');
  const [isCreditEnforcementEnabled] = CustomerLifecycleManagementOrg.useWetag('air-credit-enforcement');
  const { accessibleLocationData } = useAppScopeStore();
  const { hasFullPhoneAccess } = useHasPhoneSettingsAccess({
    selectedLocations: callPathDetails ? [accessibleLocationData[callPathDetails.locationId]] : [],
  });
  if (!callPathDetails) return;

  const subtitle = callPathDetails.officePhoneNumberName
    ? `${formatPhoneNumber(callPathDetails.officePhoneNumber)} ${callPathDetails.officePhoneNumberName}`
    : `${formatPhoneNumber(callPathDetails.officePhoneNumber)}`;
  const startTime = formatTime(callPathDetails.startedAt);

  let callerName;
  callerName = callPathDetails.patient && getFullName<GetCallPathResponse_Person>(callPathDetails.patient);
  if (!callerName) callerName = t('Caller');

  const userName = callPathDetails.user?.firstName || t('Office User');

  return (
    <>
      <EventBox iconName='phone-incoming' title={t('Incoming Call')} subtitle={subtitle} endAdornment={startTime} />
      {callPathDetails.callEvents?.map((event, index, array) => {
        const isLast = index === array.length - 1;
        // https://linear.app/getweave/issue/PE-5753/qa-call-path-mapping-skeleton#comment-0bb63dbf
        const isBeforeAvailableData = isBefore(new Date(callPathDetails.startedAt), new Date(2025, 8, 24));
        const shouldShowIcon = !isLast && showIcon(event, index, array) && isBeforeAvailableData;
        const isSecondToLast = index === array.length - 2;

        return (
          <>
            {renderEvent(event, {
              callerName,
              userName,
              isSecondToLast,
              isCreditEnforcementEnabled,
              hasFullPhoneAccess,
            })}
            {shouldShowIcon && <EventIcon />}
          </>
        );
      })}
    </>
  );
};

CallPathMapping.Header = CallPathMappingHeader;

const EventIcon = () => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip();
  const { t } = useTranslation('calls');
  return (
    <>
      <Text
        className='event-box-item analytics-info-icon'
        css={{ margin: '4px auto', position: 'relative' }}
        textAlign='center'
        as='div'
        {...triggerProps}
      >
        <Icon name='analytics-info-small' css={{ color: theme.colors.neutral70 }} />
      </Text>
      <Tooltip {...tooltipProps}>
        {t('There may be additional events here. More events will be available as we continue enhancing Call Paths.')}
      </Tooltip>
    </>
  );
};

const showIcon = (event: CallEvent, index: number, events: CallEvent[]): boolean => {
  const nextEvent = events[index + 1]?.eventType;
  if (!nextEvent) return false;

  switch (event.eventType) {
    case EventType.EVENT_TYPE_CONDITION: {
      return [EventType.EVENT_TYPE_DEVICE, EventType.EVENT_TYPE_FORWARDED, EventType.EVENT_TYPE_CALL_ENDED].includes(
        nextEvent
      );
    }

    case EventType.EVENT_TYPE_IVR: {
      if (!('ivrEvent' in event)) return false;

      const { dtmfEvents = [], dialOption } = event.ivrEvent;

      const hasDTMF = dtmfEvents.length > 0;
      const isValidNextEvent = [
        EventType.EVENT_TYPE_IVR,
        EventType.EVENT_TYPE_FORWARDED,
        EventType.EVENT_TYPE_DEVICE,
        EventType.EVENT_TYPE_CALL_ENDED,
      ].includes(nextEvent);

      if (hasDTMF) return isValidNextEvent;

      const isFallback = dialOption === 'FALLBACK';
      const isValidFallbackNext = [EventType.EVENT_TYPE_DEVICE, EventType.EVENT_TYPE_CALL_ENDED].includes(nextEvent);

      return isFallback && isValidFallbackNext;
    }

    default:
      return false;
  }
};
